import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CardActions,
  Button, Chip, Alert, CircularProgress, Divider,
  List, ListItem, ListItemText, ListItemIcon, ListItemSecondaryAction,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, Switch,
  Tooltip, Badge, Tabs, Tab, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Avatar,
  LinearProgress, Accordion, AccordionSummary, AccordionDetails,
  AlertTitle
} from '@mui/material';
import {
  Security as SecurityIcon,
  Videocam as CameraIcon,
  People as PeopleIcon,
  DoorFront as DoorIcon,
  Settings as SettingsIcon,
  Refresh as RefreshIcon,
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  Visibility as ViewIcon,
  VisibilityOff as HideIcon,
  CheckCircle as OnlineIcon,
  Error as OfflineIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Wifi as WifiIcon,
  Router as RouterIcon,
  DeviceHub as SwitchIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Event as EventIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  RecordVoiceOver as RecordIcon,
  PhotoCamera as PhotoIcon
} from '@mui/icons-material';
import ModernLayout from '../common/ModernLayout';
import ModernCard, { StatsCard } from '../common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from '../common/ModernButton';
import { useLanguage } from '../../contexts/LanguageContext';
import UniFiAPIService from './UniFiAPIService';
import UniFiCloudAPIService from './UniFiCloudAPIService';

const UniFiManagement = () => {
  const { translate } = useLanguage();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [connectionType, setConnectionType] = useState(null);
  const [unifiService, setUnifiService] = useState(null);
  
  // Data states
  const [networkDevices, setNetworkDevices] = useState([]);
  const [accessDevices, setAccessDevices] = useState([]);
  const [protectCameras, setProtectCameras] = useState([]);
  const [accessLogs, setAccessLogs] = useState([]);
  const [clients, setClients] = useState([]);
  const [systemStatus, setSystemStatus] = useState({});
  
  // UI states
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [showDeviceDialog, setShowDeviceDialog] = useState(false);
  const [showCameraDialog, setShowCameraDialog] = useState(false);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    initializeUniFi();
  }, []);

  const initializeUniFi = async () => {
    setLoading(true);
    try {
      // Check which type of UniFi connection is configured
      const cloudConfig = localStorage.getItem('unifiCloudConfig');
      const controllerConfig = localStorage.getItem('unifiConfig');
      
      if (cloudConfig) {
        console.log('Initializing UniFi Cloud connection...');
        const config = JSON.parse(cloudConfig);
        const cloudService = new UniFiCloudAPIService();
        cloudService.initialize(config);
        
        // Test connectivity first
        const connectivityTest = await cloudService.testConnectivity();
        if (!connectivityTest.success) {
          console.error('UniFi Cloud connectivity test failed:', connectivityTest.error);
          throw new Error(`Connectivity test failed: ${connectivityTest.error}`);
        }
        
        // Test API connection
        const connectionTest = await cloudService.testConnection();
        if (!connectionTest.success) {
          console.error('UniFi Cloud API test failed:', connectionTest.error);
          throw new Error(`API connection failed: ${connectionTest.error}`);
        }
        
        setUnifiService(cloudService);
        setConnectionType('cloud');
        console.log('UniFi Cloud initialized successfully');
        
        // Load data with error handling
        await loadAllData(cloudService);
      } else if (controllerConfig) {
        console.log('Initializing UniFi Controller connection...');
        const config = JSON.parse(controllerConfig);
        const controllerService = new UniFiAPIService();
        controllerService.initialize(config);
        setUnifiService(controllerService);
        setConnectionType('controller');
        console.log('UniFi Controller initialized successfully');
        
        // Load data with error handling
        await loadAllData(controllerService);
      } else {
        console.log('No UniFi configuration found');
        throw new Error('No UniFi configuration found. Please run the setup wizard first.');
      }
    } catch (error) {
      console.error('Error initializing UniFi:', error);
      // Show user-friendly error message
      setError(`UniFi initialization failed: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadAllData = async (service) => {
    try {
      console.log('Loading UniFi data...');
      
      const [
        networkResult,
        accessResult,
        protectResult,
        logsResult,
        clientsResult,
        statusResult
      ] = await Promise.allSettled([
        service.getDevices(),
        service.getAccessDevices(),
        service.getProtectCameras(),
        service.getAccessLogs(),
        service.getClients(),
        service.getSystemStatus()
      ]);

      // Handle each result individually
      if (networkResult.status === 'fulfilled' && networkResult.value.success) {
        setNetworkDevices(networkResult.value.data || []);
        console.log('Network devices loaded:', networkResult.value.data?.length || 0);
      } else {
        console.error('Failed to load network devices:', networkResult.reason || networkResult.value?.error);
      }

      if (accessResult.status === 'fulfilled' && accessResult.value.success) {
        setAccessDevices(accessResult.value.data || []);
        console.log('Access devices loaded:', accessResult.value.data?.length || 0);
      } else {
        console.error('Failed to load access devices:', accessResult.reason || accessResult.value?.error);
      }

      if (protectResult.status === 'fulfilled' && protectResult.value.success) {
        setProtectCameras(protectResult.value.data || []);
        console.log('Protect cameras loaded:', protectResult.value.data?.length || 0);
      } else {
        console.error('Failed to load protect cameras:', protectResult.reason || protectResult.value?.error);
      }

      if (logsResult.status === 'fulfilled' && logsResult.value.success) {
        setAccessLogs(logsResult.value.data || []);
        console.log('Access logs loaded:', logsResult.value.data?.length || 0);
      } else {
        console.error('Failed to load access logs:', logsResult.reason || logsResult.value?.error);
      }

      if (clientsResult.status === 'fulfilled' && clientsResult.value.success) {
        setClients(clientsResult.value.data || []);
        console.log('Clients loaded:', clientsResult.value.data?.length || 0);
      } else {
        console.error('Failed to load clients:', clientsResult.reason || clientsResult.value?.error);
      }

      if (statusResult.status === 'fulfilled' && statusResult.value.success) {
        setSystemStatus(statusResult.value.data || {});
        console.log('System status loaded');
      } else {
        console.error('Failed to load system status:', statusResult.reason || statusResult.value?.error);
      }

      console.log('UniFi data loading completed');
    } catch (error) {
      console.error('Error loading UniFi data:', error);
    }
  };

  const handleRefresh = async () => {
    if (unifiService) {
      await loadAllData(unifiService);
    }
  };

  const handleUnlockDoor = async (deviceId) => {
    try {
      const result = await unifiService.unlockDoor(deviceId);
      if (result.success) {
        // Refresh access devices
        const accessResult = await unifiService.getAccessDevices();
        if (accessResult.success) setAccessDevices(accessResult.data || []);
      }
    } catch (error) {
      console.error('Error unlocking door:', error);
    }
  };

  const handleLockDoor = async (deviceId) => {
    try {
      const result = await unifiService.lockDoor(deviceId);
      if (result.success) {
        // Refresh access devices
        const accessResult = await unifiService.getAccessDevices();
        if (accessResult.success) setAccessDevices(accessResult.data || []);
      }
    } catch (error) {
      console.error('Error locking door:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getDeviceStatusIcon = (status) => {
    switch (status) {
      case 'online':
      case 'connected':
        return <OnlineIcon color="success" />;
      case 'offline':
      case 'disconnected':
        return <OfflineIcon color="error" />;
      default:
        return <WarningIcon color="warning" />;
    }
  };

  const getDeviceTypeIcon = (type) => {
    switch (type) {
      case 'uap':
      case 'access_point':
        return <WifiIcon />;
      case 'usw':
      case 'switch':
        return <SwitchIcon />;
      case 'usg':
      case 'gateway':
        return <RouterIcon />;
      case 'udm':
      case 'dream_machine':
        return <RouterIcon />;
      default:
        return <SettingsIcon />;
    }
  };

  const renderNetworkDevices = () => (
    <Grid container spacing={3}>
      {networkDevices.map((device) => (
        <Grid item xs={12} sm={6} md={4} key={device.mac}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {getDeviceTypeIcon(device.type)}
                <Box sx={{ ml: 1, flexGrow: 1 }}>
                  <Typography variant="h6">{device.name || device.mac}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {device.type} • {device.model}
                  </Typography>
                </Box>
                {getDeviceStatusIcon(device.state)}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">IP Address:</Typography>
                <Typography variant="body2" fontWeight="bold">{device.ip}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">MAC Address:</Typography>
                <Typography variant="body2" fontFamily="monospace">{device.mac}</Typography>
              </Box>
              
              {device.tx_bytes && device.rx_bytes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>Traffic</Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(device.tx_bytes / (device.tx_bytes + device.rx_bytes)) * 100} 
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="caption">
                    TX: {(device.tx_bytes / 1024 / 1024).toFixed(1)} MB | 
                    RX: {(device.rx_bytes / 1024 / 1024).toFixed(1)} MB
                  </Typography>
                </Box>
              )}
            </CardContent>
            <CardActions>
              <Button size="small" onClick={() => {
                setSelectedDevice(device);
                setShowDeviceDialog(true);
              }}>
                View Details
              </Button>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderAccessDevices = () => (
    <Grid container spacing={3}>
      {accessDevices.map((device) => (
        <Grid item xs={12} sm={6} md={4} key={device.mac}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon />
                <Box sx={{ ml: 1, flexGrow: 1 }}>
                  <Typography variant="h6">{device.name || device.mac}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {device.type} • {device.model}
                  </Typography>
                </Box>
                {getDeviceStatusIcon(device.state)}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Location:</Typography>
                <Typography variant="body2" fontWeight="bold">{device.location || 'Unknown'}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Status:</Typography>
                <Chip 
                  label={device.locked ? 'Locked' : 'Unlocked'} 
                  color={device.locked ? 'error' : 'success'}
                  size="small"
                />
              </Box>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                color={device.locked ? 'success' : 'error'}
                onClick={() => device.locked ? handleUnlockDoor(device.mac) : handleLockDoor(device.mac)}
                startIcon={device.locked ? <UnlockIcon /> : <LockIcon />}
              >
                {device.locked ? 'Unlock' : 'Lock'}
              </Button>
              <Button size="small" onClick={() => {
                setSelectedDevice(device);
                setShowDeviceDialog(true);
              }}>
                View Details
              </Button>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderProtectCameras = () => (
    <Grid container spacing={3}>
      {protectCameras.map((camera) => (
        <Grid item xs={12} sm={6} md={4} key={camera.mac}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CameraIcon />
                <Box sx={{ ml: 1, flexGrow: 1 }}>
                  <Typography variant="h6">{camera.name || camera.mac}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {camera.type} • {camera.model}
                  </Typography>
                </Box>
                {getDeviceStatusIcon(camera.state)}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Location:</Typography>
                <Typography variant="body2" fontWeight="bold">{camera.location || 'Unknown'}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Recording:</Typography>
                <Chip 
                  label={camera.recording ? 'Active' : 'Inactive'} 
                  color={camera.recording ? 'success' : 'default'}
                  size="small"
                />
              </Box>
              
              {camera.motion_detected && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Motion detected
                </Alert>
              )}
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                onClick={() => {
                  setSelectedCamera(camera);
                  setShowCameraDialog(true);
                }}
                startIcon={<ViewIcon />}
              >
                View Camera
              </Button>
              <Button 
                size="small" 
                color={camera.recording ? 'error' : 'success'}
                startIcon={camera.recording ? <StopIcon /> : <RecordIcon />}
              >
                {camera.recording ? 'Stop Recording' : 'Start Recording'}
              </Button>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderAccessLogs = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>User</TableCell>
            <TableCell>Device</TableCell>
            <TableCell>Action</TableCell>
            <TableCell>Time</TableCell>
            <TableCell>Location</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {accessLogs.map((log, index) => (
            <TableRow key={index}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                    <PersonIcon />
                  </Avatar>
                  {log.user_name || log.user_email || 'Unknown'}
                </Box>
              </TableCell>
              <TableCell>{log.device_name || log.device_mac}</TableCell>
              <TableCell>
                <Chip 
                  label={log.action} 
                  color={log.action === 'granted' ? 'success' : 'error'}
                  size="small"
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TimeIcon sx={{ mr: 0.5, fontSize: 16 }} />
                  {new Date(log.timestamp).toLocaleString()}
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationIcon sx={{ mr: 0.5, fontSize: 16 }} />
                  {log.location || 'Unknown'}
                </Box>
              </TableCell>
              <TableCell>
                {getDeviceStatusIcon(log.status)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderConnectedClients = () => (
    <Grid container spacing={3}>
      {clients.map((client) => (
        <Grid item xs={12} sm={6} md={4} key={client.mac}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon />
                <Box sx={{ ml: 1, flexGrow: 1 }}>
                  <Typography variant="h6">{client.hostname || client.name || 'Unknown Device'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {client.ip} • {client.mac}
                  </Typography>
                </Box>
                {getDeviceStatusIcon(client.state)}
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Connected to:</Typography>
                <Typography variant="body2" fontWeight="bold">{client.ap_name || 'Unknown AP'}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Signal:</Typography>
                <Typography variant="body2" fontWeight="bold">{client.signal} dBm</Typography>
              </Box>
              
              {client.tx_bytes && client.rx_bytes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>Data Usage</Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(client.tx_bytes / (client.tx_bytes + client.rx_bytes)) * 100} 
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="caption">
                    TX: {(client.tx_bytes / 1024 / 1024).toFixed(1)} MB | 
                    RX: {(client.rx_bytes / 1024 / 1024).toFixed(1)} MB
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return renderNetworkDevices();
      case 1:
        return renderAccessDevices();
      case 2:
        return renderProtectCameras();
      case 3:
        return renderAccessLogs();
      case 4:
        return renderConnectedClients();
      default:
        return renderNetworkDevices();
    }
  };

  const tabLabels = [
    { label: 'Network Devices', icon: <WifiIcon />, count: networkDevices.length },
    { label: 'Access Control', icon: <SecurityIcon />, count: accessDevices.length },
    { label: 'Cameras', icon: <CameraIcon />, count: protectCameras.length },
    { label: 'Access Logs', icon: <TimeIcon />, count: accessLogs.length },
    { label: 'Connected Clients', icon: <PeopleIcon />, count: clients.length }
  ];

  if (loading) {
    return (
      <ModernLayout title="UniFi Management">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </ModernLayout>
    );
  }

  if (!unifiService) {
    return (
      <ModernLayout title="UniFi Management">
        <Alert severity="warning">
          No UniFi configuration found. Please configure UniFi integration first.
        </Alert>
      </ModernLayout>
    );
  }

  return (
    <ModernLayout title="UniFi Management">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
              UniFi Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage your UniFi network, access control, and cameras
            </Typography>
          </Box>
          <GradientButton
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Refresh
          </GradientButton>
        </Box>

        {/* Connection Status */}
        <Alert severity="info" sx={{ mb: 3 }}>
          Connected to UniFi {connectionType === 'cloud' ? 'Cloud' : 'Controller'}
          <Box sx={{ mt: 1 }}>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => window.location.href = '/unifi-diagnostics'}
              sx={{ mr: 1 }}
            >
              Run Diagnostics
            </Button>
            <Button 
              size="small" 
              variant="outlined" 
              onClick={() => window.location.href = '/unifi-setup-wizard'}
            >
              Setup Wizard
            </Button>
          </Box>
        </Alert>

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            <AlertTitle>Connection Error</AlertTitle>
            {error}
            <Box sx={{ mt: 1 }}>
              <Button 
                size="small" 
                variant="outlined" 
                onClick={() => window.location.href = '/unifi-setup-wizard'}
                sx={{ mr: 1 }}
              >
                Run Setup Wizard
              </Button>
              <Button 
                size="small" 
                variant="outlined" 
                onClick={() => initializeUniFi()}
              >
                Retry Connection
              </Button>
            </Box>
          </Alert>
        )}
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Network Devices"
            value={networkDevices.length}
            icon={<WifiIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Access Devices"
            value={accessDevices.length}
            icon={<SecurityIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Cameras"
            value={protectCameras.length}
            icon={<CameraIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Connected Clients"
            value={clients.length}
            icon={<PeopleIcon />}
            color="info"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          {tabLabels.map((tab, index) => (
            <Tab
              key={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {tab.icon}
                  <Typography sx={{ ml: 1 }}>{tab.label}</Typography>
                  <Badge badgeContent={tab.count} color="primary" sx={{ ml: 1 }} />
                </Box>
              }
            />
          ))}
        </Tabs>
      </Box>

      {/* Tab Content */}
      {renderTabContent()}

      {/* Device Details Dialog */}
      <Dialog
        open={showDeviceDialog}
        onClose={() => setShowDeviceDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Device Details - {selectedDevice?.name || selectedDevice?.mac}
        </DialogTitle>
        <DialogContent>
          {selectedDevice && (
            <Box>
              <Typography variant="h6" gutterBottom>Device Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Name:</Typography>
                  <Typography variant="body1">{selectedDevice.name || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Type:</Typography>
                  <Typography variant="body1">{selectedDevice.type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Model:</Typography>
                  <Typography variant="body1">{selectedDevice.model}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status:</Typography>
                  <Chip 
                    label={selectedDevice.state} 
                    color={selectedDevice.state === 'online' ? 'success' : 'error'}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">IP Address:</Typography>
                  <Typography variant="body1">{selectedDevice.ip}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">MAC Address:</Typography>
                  <Typography variant="body1" fontFamily="monospace">{selectedDevice.mac}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeviceDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Camera View Dialog */}
      <Dialog
        open={showCameraDialog}
        onClose={() => setShowCameraDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Camera View - {selectedCamera?.name || selectedCamera?.mac}
        </DialogTitle>
        <DialogContent>
          {selectedCamera && (
            <Box>
              <Typography variant="h6" gutterBottom>Camera Information</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Name:</Typography>
                  <Typography variant="body1">{selectedCamera.name || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Type:</Typography>
                  <Typography variant="body1">{selectedCamera.type}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Location:</Typography>
                  <Typography variant="body1">{selectedCamera.location || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Recording:</Typography>
                  <Chip 
                    label={selectedCamera.recording ? 'Active' : 'Inactive'} 
                    color={selectedCamera.recording ? 'success' : 'default'}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Camera feed would be displayed here
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Button startIcon={<PhotoIcon />} sx={{ mr: 1 }}>
                    Take Snapshot
                  </Button>
                  <Button startIcon={<RecordIcon />}>
                    Start Recording
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCameraDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </ModernLayout>
  );
};

export default UniFiManagement; 